import { Article } from '@wix/answers-api';
import { Box, Text, TextButton } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import moment from 'moment';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { FunctionComponent } from 'react';

import 'moment/min/locales';
import { TOOLTIP_ENTER_DELAY } from '../../../../constants';
import { DATA_HOOKS } from '../../../../dataHooks';
import { OpenInNewTabIcon } from '../../../../icons/OpenInNewTabIcon';
import { InnerLink } from '../../../InnerLink';

import css from './index.module.scss';

export type FollowedContentListProps = {
  followedContent: Article[];
  unfollowContnet: (articleId: string) => void;
};

export const FollowedContentList: FunctionComponent<
  FollowedContentListProps
> = ({ followedContent, unfollowContnet }) => {
  const { locale } = useRouter();
  const { t } = useTranslation();

  const formatDate = (data?: number | Date) =>
    moment(data)
      .locale(locale as string)
      .format('LL');

  const onUnfollowContnetClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    articleId: string
  ) => {
    e.stopPropagation();
    e.preventDefault();
    unfollowContnet(articleId);
  };

  return (
    <Box
      direction="vertical"
      dataHook={DATA_HOOKS.MY_ACTIVITY_FOLLOWED_CONTENT_LIST}
    >
      <ul className={css.list}>
        {followedContent?.map((item, idx) => (
          <li key={item.id} className={css.listItem}>
            <InnerLink
              dataHook={DATA_HOOKS.MY_ACTIVITY_FOLLOWED_CONTENT_ITEM}
              href={item.uri}
            >
              <Box direction="horizontal" verticalAlign="middle">
                <Image
                  src={'https://static.parastorage.com/services/kb-client-nextjs/dist/public/images/icons/followed-article.svg'}
                  width="29"
                  height="31"
                  alt=""
                  className={css.listIcon}
                  loading="eager"
                />
                <Box
                  overflow="hidden"
                  direction="vertical"
                  verticalAlign="middle"
                  align="space-between"
                >
                  <Text
                    size="medium"
                    ellipsis={true}
                    tooltipProps={{ enterDelay: TOOLTIP_ENTER_DELAY }}
                    dataHook={
                      DATA_HOOKS.MY_ACTIVITY_FOLLOWED_CONTENT_ITEM_TITLE
                    }
                    className={css.title}
                  >
                    {item.title}
                  </Text>
                  <Text size="tiny" className={css.creationDate}>
                    {formatDate(item.lastUpdateDate)}
                  </Text>
                </Box>
              </Box>
            </InnerLink>
            <Box
              className={css.actionRow}
              direction="horizontal"
              align="center"
              verticalAlign="middle"
              gap={'10px'}
            >
              <InnerLink href={item.uri} className={css.open}>
                <Box
                  direction="horizontal"
                  align="center"
                  verticalAlign="middle"
                >
                  <OpenInNewTabIcon />
                  {t('user.my-activity.open')}
                </Box>
              </InnerLink>
              <TextButton
                weight="normal"
                size="small"
                dataHook={
                  DATA_HOOKS.MY_ACTIVITY_FOLLOWED_CONTENT_ITEM_UNFOLLOW + idx
                }
                onClick={(e: any) => onUnfollowContnetClick(e, item.id)}
                className={css.unfollow}
              >
                {t('user.my-activity.unfollow')}
              </TextButton>
            </Box>
          </li>
        ))}
      </ul>
    </Box>
  );
};
